/**
 * Offline / background catch-up (tech-design §2.4): "closing the tab is a valid way
 * to play." When the player returns after an absence we advance the simulation by the
 * elapsed wall-clock time — but through the *exact same* {@link tick} code path as the
 * live loop, so there is no second economy to keep in sync.
 *
 * This module is pure and headless: wall-clock time enters only as an `elapsedSec`
 * argument (measured by the app layer), never read here. That keeps the whole catch-up
 * deterministic and unit-testable in Node.
 *
 * The absence is sliced into ~{@link OFFLINE_CHUNKS} coarse ticks rather than one giant
 * tick so that threshold crossings that happen mid-absence — milestones, and the
 * production boosts they unlock — are honored at chunk resolution instead of all
 * snapping at the end. Breakthrough rolls are deliberately *never* auto-attempted:
 * `tick()` doesn't touch them, and they stay a deliberate player moment.
 */
import { ZERO, sub, type Num } from './num.js';
import type { Content } from './content.js';
import { offlineCapSec } from './selectors.js';
import { getResource, type GameState } from './state.js';
import { tick } from './tick.js';

/** Logic ticks per second — the fixed 100 ms timestep (must match {@link tick}). */
export const TICKS_PER_SEC = 10;

/** Default cap on catch-up: 8 hours (tech-design §2.4), extensible via `offline.cap`. */
export const DEFAULT_OFFLINE_CAP_SEC = 8 * 60 * 60;

/** How many coarse ticks a long absence is sliced into (mid-absence unlock resolution). */
export const OFFLINE_CHUNKS = 1000;

export interface OfflineOptions {
  /** Override the computed cap (seconds). Defaults to {@link offlineCapSec}. */
  readonly capSec?: number;
  /** Override the chunk count. Defaults to {@link OFFLINE_CHUNKS}. */
  readonly chunks?: number;
}

export interface OfflineResult {
  /** State after the catch-up. */
  readonly state: GameState;
  /** Raw elapsed time reported by the caller, before capping (seconds). */
  readonly requestedSec: number;
  /** Elapsed time actually simulated after the cap (seconds). */
  readonly elapsedSec: number;
  /** True when `requestedSec` exceeded the cap (the modal notes the lost time). */
  readonly capped: boolean;
  /** Whole logic ticks simulated (`elapsedSec * TICKS_PER_SEC`, floored). */
  readonly ticks: number;
  /** Net per-resource change (after − before); positive is a gain, negative a spend. */
  readonly resourceDelta: Readonly<Record<string, Num>>;
  /** Milestones that became true during the absence (chunk resolution). */
  readonly milestones: readonly string[];
}

/**
 * Advance `state` by `elapsedSec` of absence, capped and chunked. Pure: the same
 * inputs always yield the same {@link OfflineResult}. A zero/negative elapsed (or a
 * clock that went backwards) simulates nothing and returns the state untouched.
 */
export function simulateOffline(
  state: GameState,
  content: Content,
  elapsedSec: number,
  options: OfflineOptions = {},
): OfflineResult {
  const requestedSec = Number.isFinite(elapsedSec) && elapsedSec > 0 ? elapsedSec : 0;
  const capSec = options.capSec ?? offlineCapSec(state, content, DEFAULT_OFFLINE_CAP_SEC);
  const cappedSec = Math.min(requestedSec, Math.max(0, capSec));
  const totalTicks = Math.floor(cappedSec * TICKS_PER_SEC);

  const advanced = runChunked(
    state,
    content,
    totalTicks,
    options.chunks ?? OFFLINE_CHUNKS,
  );

  return {
    state: advanced,
    requestedSec,
    elapsedSec: cappedSec,
    capped: requestedSec > capSec,
    ticks: totalTicks,
    resourceDelta: diffResources(state, advanced),
    milestones: newMilestones(state, advanced),
  };
}

/**
 * Drive `tick()` over `totalTicks`, split into at most `chunks` slices of near-equal
 * size (each slice ≥ 1 tick). Even division so the slices sum to exactly `totalTicks`;
 * when `totalTicks < chunks` every carried slice is a single tick.
 */
function runChunked(
  state: GameState,
  content: Content,
  totalTicks: number,
  chunks: number,
): GameState {
  if (totalTicks <= 0) return state;
  const slices = Math.min(Math.max(1, chunks), totalTicks);
  let current = state;
  let remaining = totalTicks;
  for (let i = 0; i < slices; i += 1) {
    const step = Math.floor(remaining / (slices - i));
    current = tick(current, content, step);
    remaining -= step;
  }
  return current;
}

/** Net resource change across the catch-up, keyed by resource id (zero deltas omitted). */
function diffResources(before: GameState, after: GameState): Record<string, Num> {
  const delta: Record<string, Num> = {};
  const ids = new Set<string>([
    ...Object.keys(before.resources),
    ...Object.keys(after.resources),
  ]);
  for (const id of ids) {
    const change = sub(getResource(after, id).amount, getResource(before, id).amount);
    if (!change.eq(ZERO)) delta[id] = change;
  }
  return delta;
}

/** Milestone ids present after but not before — "notable unlocks" for the modal. */
function newMilestones(before: GameState, after: GameState): string[] {
  const had = new Set(before.milestones);
  return after.milestones.filter((id) => !had.has(id));
}
